import React, { useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { CustomSeriesRenderItem, EChartsOption } from 'echarts';

const FONT = 'Montserrat, sans-serif';
const SVG_RENDERER = { renderer: 'svg' as const };

const tooltip = {
  trigger: 'item' as const,
  confine: true,
  backgroundColor: '#fff',
  borderColor: '#d9e2ef',
  borderWidth: 1,
  padding: [6, 10],
  textStyle: { color: '#003b63', fontFamily: FONT, fontSize: 12 },
  extraCssText: 'box-shadow: 0 2px 8px rgba(0, 59, 99, .18);',
};

const lighten = (hex: string, amount: number) => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(n >> 16)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

type BarDatum = { label: string; value: number; display: string };

type BarsProps = {
  data: BarDatum[];
  color: string;
  hoverColor: string;
  axisColor: string;
  rowHeight: number;
  barHeight: number;
  fontSize: number;
  labelFontSize: number;
  labelWidth: number;
  ariaLabel: string;
};

// A horizontal cylinder: rounded start, flat body and a lighter elliptical end cap.
const cylinderBars = (
  data: BarDatum[], color: string, hoverColor: string, barHeight: number, labelFontSize: number,
): CustomSeriesRenderItem => ((params: { dataIndex: number }, api: {
  coord(point: number[]): number[]; value(dim: number): number; }) => {
  const [x1, yc] = api.coord([api.value(0), api.value(1)]);
  const [x0] = api.coord([0, api.value(1)]);
  const start = barHeight * 0.3;
  const end = barHeight * 0.28;
  const bodyWidth = Math.max(0, x1 - end - (x0 + start));
  const part = (fill: string, hover: string) => ({ style: { fill }, emphasis: { style: { fill: hover } } });
  return {
    type: 'group' as const,
    children: [
      { type: 'ellipse' as const, shape: { cx: x0 + start, cy: yc, rx: start, ry: barHeight / 2 }, ...part(color, hoverColor) },
      { type: 'rect' as const, shape: { x: x0 + start, y: yc - barHeight / 2, width: bodyWidth, height: barHeight }, ...part(color, hoverColor) },
      {
        type: 'ellipse' as const,
        shape: { cx: x1 - end, cy: yc, rx: end, ry: barHeight / 2 },
        ...part(lighten(color, 0.45), lighten(hoverColor, 0.45)),
      },
      {
        type: 'text' as const,
        silent: true,
        style: {
          text: data[params.dataIndex].display,
          x: x1 + 8,
          y: yc,
          fill: '#444',
          fontFamily: FONT,
          fontSize: labelFontSize,
          textVerticalAlign: 'middle' as const,
        },
      },
    ],
  };
}) as unknown as CustomSeriesRenderItem;

export const HorizontalBars = ({
  data, color, hoverColor, axisColor, rowHeight, barHeight, fontSize, labelFontSize, labelWidth, ariaLabel,
}: BarsProps) => {
  const option = useMemo<EChartsOption>(() => ({
    animationDuration: 500,
    grid: { left: 0, right: labelWidth, top: 4, bottom: 4, containLabel: true },
    tooltip: { ...tooltip, formatter: (p: any) => `${p.name}: <b>${data[p.dataIndex].display}</b>` },
    xAxis: { type: 'value', show: false, max: Math.max(...data.map((d) => d.value)) },
    yAxis: {
      type: 'category',
      inverse: true,
      data: data.map((d) => d.label),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: axisColor } },
      axisLabel: { color: '#555', fontFamily: FONT, fontSize, margin: 8 },
    },
    series: [{
      type: 'custom',
      renderItem: cylinderBars(data, color, hoverColor, barHeight, labelFontSize),
      encode: { x: 0, y: 1 },
      data: data.map((d, i) => ({ name: d.label, value: [d.value, i] })),
    }],
  }), [data, color, hoverColor, axisColor, barHeight, fontSize, labelFontSize, labelWidth]);

  return (
    <div role="img" aria-label={ariaLabel}>
      <ReactECharts
        option={option}
        opts={SVG_RENDERER}
        style={{ height: data.length * rowHeight + 8, width: '100%' }}
      />
    </div>
  );
};

// `wall` and `inner` are evenly spaced colour stops, left to right, for the outer and hole walls.
export type SliceDatum = { name: string; value: number; color: string; wall?: string[]; inner?: string[] };

const horizontalGradient = (stops: string[], x0: number, x1: number) => ({
  type: 'linear' as const,
  x: x0,
  y: 0,
  x2: x1,
  y2: 0,
  global: true,
  colorStops: stops.map((color, i) => ({ offset: i / (stops.length - 1), color })),
});

const arc = (cx: number, cy: number, rx: number, ry: number, from: number, to: number, dy = 0) => {
  const steps = Math.max(2, Math.ceil(Math.abs(to - from) / (Math.PI / 90)));
  return Array.from({ length: steps + 1 }, (_, i) => {
    const a = from + ((to - from) * i) / steps;
    return [cx + rx * Math.sin(a), cy - ry * Math.cos(a) + dy];
  });
};

// Angles run clockwise from 12 o'clock; the lower half faces the viewer.
const slices3D = (items: SliceDatum[]): CustomSeriesRenderItem => {
  const total = items.reduce((sum, d) => sum + d.value, 0);
  const bounds = items.map((_, i) => {
    const before = items.slice(0, i).reduce((sum, d) => sum + d.value, 0);
    return [(before / total) * 2 * Math.PI, ((before + items[i].value) / total) * 2 * Math.PI];
  });

  return ((params: { dataIndex: number }, api: { getWidth(): number; getHeight(): number }) => {
    const [a0, a1] = bounds[params.dataIndex];
    const { color, wall = [color, color], inner = [color, color] } = items[params.dataIndex];
    const depth = Math.min(api.getHeight() * 0.19, 34);
    const rx = api.getWidth() / 2 - 6;
    const ry = Math.min(rx * 0.72, (api.getHeight() - depth) / 2 - 4);
    const cx = api.getWidth() / 2;
    const cy = (api.getHeight() - depth) / 2;
    const irx = rx * 0.56;
    const iry = ry * 0.56;

    // z2 layers every slice together (hole walls, outer walls, then tops), so a slice's wall never paints over another slice's top.
    const face = (points: number[][], fill: string | ReturnType<typeof horizontalGradient>, z2: number, hover?: string) => ({
      type: 'polygon' as const,
      z2,
      shape: { points },
      style: { fill, stroke: typeof fill === 'string' ? fill : 'none', lineWidth: 0.6 },
      ...(hover && { emphasis: { style: { fill: hover, stroke: hover } } }),
    });

    const children = [];
    // Far wall of the hole, visible through it.
    [[0, Math.PI / 2], [(3 * Math.PI) / 2, 2 * Math.PI]].forEach(([lo, hi]) => {
      const s = Math.max(a0, lo);
      const e = Math.min(a1, hi);
      if (e > s) {
        children.push(face(
          [...arc(cx, cy, irx, iry, s, e), ...arc(cx, cy, irx, iry, e, s, depth)],
          horizontalGradient(inner, cx - irx, cx + irx),
          1,
        ));
      }
    });
    // Outer wall, visible on the lower half only.
    const s = Math.max(a0, Math.PI / 2);
    const e = Math.min(a1, (3 * Math.PI) / 2);
    if (e > s) {
      children.push(face(
        [...arc(cx, cy, rx, ry, s, e), ...arc(cx, cy, rx, ry, e, s, depth)],
        horizontalGradient(wall, cx - rx, cx + rx),
        2,
      ));
    }
    children.push(face([...arc(cx, cy, rx, ry, a0, a1), ...arc(cx, cy, irx, iry, a1, a0)], color, 3, lighten(color, 0.18)));
    return { type: 'group' as const, children };
  }) as unknown as CustomSeriesRenderItem;
};

type DonutProps = {
  data: SliceDatum[];
  centerValue: string;
  centerLabel: string;
  centerSize: number;
  threeD?: boolean;
  className: string;
  ariaLabel: string;
  legendClassName?: string;
};

export const DonutWithLegend = ({
  data, centerValue, centerLabel, centerSize, threeD = false, className, ariaLabel, legendClassName = '',
}: DonutProps) => {
  const chartRef = useRef<ReactECharts>(null);
  const [active, setActive] = useState<number | null>(null);

  const option = useMemo<EChartsOption>(() => ({
    animationDuration: 600,
    tooltip: { ...tooltip, formatter: (p: any) => `${p.name}: <b>${data[p.dataIndex].value}%</b>` },
    title: {
      text: centerValue,
      subtext: centerLabel,
      left: 'center',
      top: threeD ? '36%' : 'center',
      itemGap: 0,
      textStyle: { color: '#111', fontFamily: FONT, fontSize: centerSize, fontWeight: 800 },
      subtextStyle: { color: '#111', fontFamily: FONT, fontSize: 15, fontWeight: 600 },
    },
    series: [threeD
      ? {
        type: 'custom',
        coordinateSystem: 'none',
        renderItem: slices3D(data),
        data: data.map((d) => ({ name: d.name, value: d.value })),
      }
      : {
        type: 'pie',
        radius: ['50%', '94%'],
        startAngle: 90,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        emphasis: { scaleSize: 6, itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, .25)' } },
        data: data.map((d) => ({ name: d.name, value: d.value, itemStyle: { color: d.color } })),
      }],
  }), [data, centerValue, centerLabel, centerSize, threeD]);

  const onEvents = useMemo(() => ({
    mouseover: (e: { componentType?: string; dataIndex?: number }) => {
      if (e.componentType === 'series' && e.dataIndex !== undefined) setActive(e.dataIndex);
    },
    mouseout: () => setActive(null),
  }), []);

  const dispatch = (type: 'highlight' | 'downplay', dataIndex: number) => {
    chartRef.current?.getEchartsInstance().dispatchAction({ type, seriesIndex: 0, dataIndex });
  };

  return (
    <>
      <div className={className} role="img" aria-label={ariaLabel}>
        <ReactECharts
          ref={chartRef}
          option={option}
          opts={SVG_RENDERER}
          onEvents={onEvents}
          style={{ height: '100%', width: '100%' }}
        />
      </div>
      <ul className={`about-pepwise__legend ${legendClassName}`}>
        {data.map(({ name, value, color }, i) => (
          <li
            key={name}
            className={active === i ? 'is-active' : undefined}
            onMouseEnter={() => { setActive(i); dispatch('highlight', i); }}
            onMouseLeave={() => { setActive(null); dispatch('downplay', i); }}
          >
            <i style={{ backgroundColor: color }} /><span>{name}</span><strong>{value}%</strong>
          </li>
        ))}
      </ul>
    </>
  );
};
